import React from 'react';
import { QRCodeSVG as QRCode } from 'qrcode.react';

/**
 * Creative Modern Theme
 * Bold design with asymmetric layout, perfect for creative industries
 */
const CreativeModernTheme = ({ document, settings = {} }) => {
  const primaryColor = settings.theme?.primary_color || '#EC4899';
  const secondaryColor = settings.theme?.secondary_color || '#8B5CF6';
  const fontFamily = settings.typography?.font_family || 'Poppins, sans-serif';
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    if (num === 0) return 'Zero Rupees Only';
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const hundred = Math.floor((num % 1000) / 100);
    const remainder = Math.floor(num % 100);
    let words = '';
    if (crore > 0) words += ones[crore] + ' Crore ';
    if (lakh > 0) words += (lakh < 20 && lakh > 9 ? teens[lakh - 10] : (tens[Math.floor(lakh / 10)] + ' ' + ones[lakh % 10])) + ' Lakh ';
    if (thousand > 0) words += (thousand < 20 && thousand > 9 ? teens[thousand - 10] : (tens[Math.floor(thousand / 10)] + ' ' + ones[thousand % 10])) + ' Thousand ';
    if (hundred > 0) words += ones[hundred] + ' Hundred ';
    if (remainder > 0) {
      if (remainder < 10) words += ones[remainder];
      else if (remainder < 20) words += teens[remainder - 10];
      else words += tens[Math.floor(remainder / 10)] + ' ' + ones[remainder % 10];
    }
    return words.trim() + ' Rupees Only';
  };

  return (
    <div className="print-template creative-modern" style={{ fontFamily, maxWidth: '210mm', margin: '0 auto', background: '#fff', position: 'relative', overflow: 'hidden' }}>
      {/* Asymmetric Colored Header */}
      <div style={{ background: `linear-gradient(120deg, ${primaryColor} 0%, ${secondaryColor} 100%)`, height: '200px', position: 'relative', clipPath: 'polygon(0 0, 100% 0, 100% 85%, 0 100%)' }}>
        <div style={{ padding: '40px', color: '#fff', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              {settings.branding?.logo_url ? (
                <div style={{ background: '#fff', padding: '12px', borderRadius: '50%', width: '80px', height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '15px' }}>
                  <img src={settings.branding.logo_url} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                </div>
              ) : (
                <div style={{ fontSize: '48px', fontWeight: '900', letterSpacing: '-2px', marginBottom: '10px' }}>Q</div>
              )}
              <h1 style={{ fontSize: '28px', fontWeight: '700', margin: 0, textShadow: '2px 2px 8px rgba(0,0,0,0.3)' }}>
                {settings.branding?.company_name || 'CREATIVE STUDIO'}
              </h1>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.25)', backdropFilter: 'blur(10px)', padding: '20px 25px', borderRadius: '20px', border: '2px solid rgba(255,255,255,0.4)' }}>
              <div style={{ fontSize: '40px', fontWeight: '900', lineHeight: 1, marginBottom: '5px' }}>{document.quotation_no || 'QT-001'}</div>
              <div style={{ fontSize: '11px', opacity: 0.95 }}>{new Date(document.doc_date || Date.now()).toLocaleDateString('en-IN')}</div>
            </div>
          </div>
        </div>
        {/* Decorative circles */}
        <div style={{ position: 'absolute', top: '20px', right: '100px', width: '150px', height: '150px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }}></div>
        <div style={{ position: 'absolute', top: '120px', right: '40px', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)' }}></div>
      </div>

      <div style={{ padding: '40px' }}>
        {/* Client Info - Offset Card */}
        <div style={{ marginTop: '-60px', marginBottom: '30px', position: 'relative', zIndex: 2 }}>
          <div style={{ background: '#fff', boxShadow: '0 10px 40px rgba(0,0,0,0.15)', borderRadius: '16px', padding: '25px', border: `3px solid ${primaryColor}` }}>
            <div style={{ display: 'flex', gap: '30px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '10px', fontWeight: '700', letterSpacing: '2px', color: primaryColor, marginBottom: '10px' }}>CLIENT</div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#1F2937', marginBottom: '8px' }}>{document.party?.name || 'Customer Name'}</div>
                {document.party?.phone && <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '4px' }}>Phone: {document.party.phone}</div>}
                {document.party?.billing_address && (
                  <div style={{ fontSize: '12px', color: '#6B7280', lineHeight: '1.6', marginTop: '8px' }}>
                    {document.party.billing_address.line1}, {document.party.billing_address.city}
                  </div>
                )}
              </div>
              {settings.qr_code_data && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <QRCode value={settings.qr_code_data} size={90} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Items - Creative Layout */}
        <div style={{ marginBottom: '30px' }}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#1F2937', marginBottom: '15px', letterSpacing: '1px', textTransform: 'uppercase', borderLeft: `4px solid ${primaryColor}`, paddingLeft: '15px' }}>
            Line Items
          </div>
          {(document.items || []).map((item, index) => (
            <div key={index} style={{ background: index % 2 === 0 ? '#F9FAFB' : '#fff', borderRadius: '12px', padding: '18px', marginBottom: '10px', border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: '15px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '14px' }}>
                {index + 1}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '15px', fontWeight: '600', color: '#1F2937', marginBottom: '3px' }}>{item.name}</div>
                {item.description && <div style={{ fontSize: '11px', color: '#9CA3AF' }}>{item.description}</div>}
              </div>
              {settings.toggles?.show_hsn && (
                <div style={{ fontSize: '11px', color: '#6B7280', width: '80px', textAlign: 'center' }}>
                  <div style={{ fontSize: '9px', color: '#9CA3AF' }}>HSN</div>
                  {item.hsn || '-'}
                </div>
              )}
              <div style={{ fontSize: '13px', color: '#6B7280', width: '80px', textAlign: 'center' }}>
                <div style={{ fontSize: '9px', color: '#9CA3AF' }}>QTY</div>
                {item.quantity} {item.unit || 'PCS'}
              </div>
              <div style={{ fontSize: '13px', color: '#6B7280', width: '100px', textAlign: 'right' }}>
                <div style={{ fontSize: '9px', color: '#9CA3AF', marginBottom: '2px' }}>RATE</div>
                {formatCurrency(item.unit_price)}
              </div>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#1F2937', width: '140px', textAlign: 'right' }}>
                {formatCurrency(item.amount)}
              </div>
            </div>
          ))}
        </div>

        {/* Totals - Creative Style */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '25px' }}>
          <div style={{ width: '500px' }}>
            <div style={{ background: '#F9FAFB', borderRadius: '16px', padding: '20px', border: '2px solid #E5E7EB' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px' }}>
                <span style={{ color: '#6B7280', fontWeight: '500' }}>Subtotal</span>
                <span style={{ fontWeight: '600', color: '#1F2937' }}>{formatCurrency(document.totals?.subtotal || 0)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px' }}>
                <span style={{ color: '#6B7280', fontWeight: '500' }}>Discount</span>
                <span style={{ fontWeight: '600', color: '#DC2626' }}>- {formatCurrency(document.totals?.discount_total || 0)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px', paddingTop: '10px', borderTop: '1px solid #E5E7EB' }}>
                <span style={{ color: '#6B7280', fontWeight: '600' }}>Taxable Amount</span>
                <span style={{ fontWeight: '700', color: '#1F2937' }}>{formatCurrency(document.totals?.taxable_amount || 0)}</span>
              </div>
              {settings.toggles?.show_tax_breakup && document.tax_breakup?.map((tax, idx) => (
                <React.Fragment key={idx}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', paddingLeft: '10px' }}>
                    <span style={{ color: '#6B7280' }}>CGST @ {tax.rate / 2}%</span>
                    <span style={{ fontWeight: '500', color: '#1F2937' }}>{formatCurrency(tax.amount / 2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', paddingLeft: '10px' }}>
                    <span style={{ color: '#6B7280' }}>SGST @ {tax.rate / 2}%</span>
                    <span style={{ fontWeight: '500', color: '#1F2937' }}>{formatCurrency(tax.amount / 2)}</span>
                  </div>
                </React.Fragment>
              ))}
              {document.totals?.round_off !== 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
                  <span style={{ color: '#6B7280' }}>Round Off</span>
                  <span style={{ fontWeight: '500', color: '#1F2937' }}>{document.totals.round_off > 0 ? '+' : ''} {formatCurrency(Math.abs(document.totals.round_off))}</span>
                </div>
              )}
            </div>
            <div style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`, borderRadius: '16px', padding: '25px', marginTop: '10px', color: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: document.payment?.received_amount ? '15px' : '0' }}>
                <span style={{ fontSize: '16px', fontWeight: '700', letterSpacing: '1px' }}>TOTAL</span>
                <span style={{ fontSize: '32px', fontWeight: '900' }}>{formatCurrency(document.totals?.grand_total || 0)}</span>
              </div>
              {document.payment?.received_amount && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', opacity: 0.95, marginBottom: '6px', borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: '12px' }}>
                    <span>Received:</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(document.payment.received_amount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', opacity: 0.95 }}>
                    <span>Balance:</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(document.totals.grand_total - document.payment.received_amount)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Amount in Words */}
        <div style={{ background: '#F9FAFB', border: `2px solid ${primaryColor}`, borderRadius: '12px', padding: '18px', marginBottom: '30px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', color: primaryColor, marginBottom: '8px', letterSpacing: '1px', textTransform: 'uppercase' }}>Amount in Words</div>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#1F2937', textTransform: 'uppercase' }}>
            {numberToWords(document.totals?.grand_total || 0)}
          </div>
        </div>

        {/* Footer */}
        <div style={{ borderTop: `3px solid ${primaryColor}`, paddingTop: '25px' }}>
          <div style={{ display: 'flex', gap: '25px', marginBottom: '30px' }}>
            {document.terms?.notes_public && (
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: primaryColor, marginBottom: '10px', letterSpacing: '1px', textTransform: 'uppercase' }}>Terms & Conditions</div>
                <div style={{ fontSize: '11px', color: '#6B7280', lineHeight: '1.8' }}>
                  {document.terms.notes_public}
                </div>
              </div>
            )}
            {settings.toggles?.show_bank_details && settings.bank_details && (
              <div style={{ width: '300px', background: '#F9FAFB', border: `2px solid ${primaryColor}`, borderRadius: '12px', padding: '18px' }}>
                <div style={{ fontSize: '11px', fontWeight: '700', color: primaryColor, marginBottom: '10px', letterSpacing: '1px', textTransform: 'uppercase' }}>Bank Details</div>
                <div style={{ fontSize: '11px', color: '#6B7280', lineHeight: '1.8' }}>
                  <div><strong>Bank:</strong> {settings.bank_details.bank_name}</div>
                  <div><strong>Account:</strong> {settings.bank_details.account_number}</div>
                  <div><strong>IFSC:</strong> {settings.bank_details.ifsc}</div>
                </div>
              </div>
            )}
          </div>
          {settings.toggles?.show_signature && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '40px' }}>
              <div>
                {settings.qr_code_data && <QRCode value={settings.qr_code_data} size={90} />}
              </div>
              <div style={{ textAlign: 'center', minWidth: '250px' }}>
                <div style={{ borderTop: `3px solid ${primaryColor}`, paddingTop: '15px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: primaryColor, letterSpacing: '1px' }}>AUTHORIZED SIGNATORY</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreativeModernTheme;